import React, { useMemo } from 'react';
import ReactECharts from 'echarts-for-react';
import type { EChartsOption } from 'echarts';
import {
  ChartComponentProps,
  getBaseOption,
  buildValueAxis,
  formatLabelFromKey,
  formatTooltipValue,
  ECHARTS_OPTS,
  isNumericLike,
  toNumericValue,
  getCategoryValue,
} from './chartConfig';

export const ScatterChart: React.FC<ChartComponentProps> = ({ chart, height = 320, colors }) => {
  const option = useMemo((): EChartsOption => {
    const data = chart.data || [];
    if (data.length === 0) return {};

    const base = getBaseOption(colors);
    const keys = Array.from(new Set(data.flatMap((row) => Object.keys(row)))).filter((key) =>
      key !== 'name' && data.some((row) => isNumericLike(row[key]))
    );
    const xKey = keys[0] || 'value';
    const yKey = keys[1] || keys[0] || 'value';
    const sizeKey = chart.type === 'bubble' && keys[2] ? keys[2] : undefined;

    const xLabel = chart.x_axis_label || formatLabelFromKey(xKey);
    const yLabel = chart.y_axis_label || formatLabelFromKey(yKey);

    const scatterData = data.map(d => {
      const point: number[] = [toNumericValue(d[xKey]), toNumericValue(d[yKey])];
      if (sizeKey) point.push(toNumericValue(d[sizeKey]));
      return point;
    });

    const maxSize = sizeKey ? Math.max(...scatterData.map(p => p[2] || 1)) : 1;

    return {
      ...base,
      xAxis: {
        type: 'value',
        name: xLabel,
        nameLocation: 'middle',
        nameGap: 35,
        nameTextStyle: { fontSize: 12, color: '#7B6B72' },
        axisLabel: { fontSize: 11, color: '#7B6B72' },
        splitLine: { lineStyle: { color: '#EDE5D8', type: 'dashed' } },
      },
      yAxis: {
        ...buildValueAxis(chart, [yKey]),
        name: yLabel,
      },
      tooltip: {
        ...(base.tooltip as object),
        trigger: 'item',
        formatter: (params: unknown) => {
          const p = params as { value?: number[]; marker?: string; dataIndex?: number };
          const d = data[p.dataIndex ?? 0];
          const name = d ? getCategoryValue(d as Record<string, unknown>) : '';
          let html = `<div style="font-size:12px;color:#F7F3EE">${name}</div>`;
          html += `<div style="font-size:12px">${p.marker}${xLabel}: ${formatTooltipValue(p.value?.[0])}</div>`;
          html += `<div style="font-size:12px">${yLabel}: ${formatTooltipValue(p.value?.[1])}</div>`;
          if (sizeKey && p.value?.[2] != null) {
            html += `<div style="font-size:12px">${formatLabelFromKey(sizeKey)}: ${formatTooltipValue(p.value[2])}</div>`;
          }
          return html;
        },
      },
      series: [
        {
          type: 'scatter',
          data: scatterData,
          symbolSize: sizeKey
            ? (val: number[]) => Math.max(6, (val[2] / maxSize) * 40)
            : 10,
          emphasis: { focus: 'self' as const },
        },
      ],
      legend: { show: false },
    };
  }, [chart, colors]);

  return <ReactECharts option={option} style={{ height }} notMerge lazyUpdate opts={ECHARTS_OPTS} />;
};
